import React, {createContext, useContext, useEffect, useRef} from 'react'
import {flowResult, when} from 'mobx'
import {
  useBasketStore,
  useCustomerStore,
  useGlobalStore,
  useProductStore
} from '../../store/hooks/useStore'
import {StatusMap} from '../../types/commerce'
import {getSecureImageUrl} from '../../utils/image-utils'
import {formatProductName} from '../../utils/utils'
import {useOneTrustCheckCompleted} from '../react-gtm-hook'
import {createBufferedDispatch} from './dispatcher'
import {startDataCloud} from './start'
import type {DataCloudDispatch} from './types'

const noop: DataCloudDispatch = () => undefined

// Basket quantity changes are debounced for 1s before they are saved
const BASKET_SETTLE_TIMEOUT_MS = 10000

const DataCloudDispatchContext = createContext<DataCloudDispatch>(noop)

export const DataCloudProvider = ({children}: {children: React.ReactNode}) => {
  const globalStore = useGlobalStore()
  const customerStore = useCustomerStore()
  const basketStore = useBasketStore()
  const productStore = useProductStore()
  const config = globalStore.dataCloudConfiguration
  const oneTrustCheckCompleted = useOneTrustCheckCompleted()
  // Stable dispatch identity so enabling Data Cloud never re-renders tracking hook consumers
  const bufferRef = useRef(createBufferedDispatch())
  const buffer = bufferRef.current

  useEffect(() => {
    if (!oneTrustCheckCompleted) return

    const stop = startDataCloud({
      config,
      getSitePreference: (id) => flowResult(globalStore.getCustomSitePreferenceById(id)),
      isOneTrustEnabled: () => globalStore.customSitePreferences.oneTrustEnabled === true,
      getContext: () => ({
        customerNo: customerStore.customerInfo?.customerNo,
        customerId: customerStore.customerInfo?.customerId,
        email: customerStore.customerInfo?.email,
        basketId: basketStore.basket?.basketId,
        orderedBasketId: basketStore.lastOrderedBasketId ?? undefined,
        basketTotals: basketStore.basket
          ? {
              totalProductAmount: Number(basketStore.basket.productSubTotal) || 0,
              adjustedTotalProductAmount: Number(basketStore.basket.productTotal) || 0,
              totalProductQuantity: basketStore.count
            }
          : undefined,
        getProductLinks: (productId) => {
          const product = productStore.productsById[productId]
          const name = formatProductName(product?.name)
          const path = name ? `${name}/${encodeURIComponent(productId)}.html` : encodeURIComponent(productId)
          return {
            imageUrl: getSecureImageUrl(product?.getImagesForStandardProduct()[0]) || undefined,
            viewUrl: `${window.location.origin}/p/${path}`
          }
        }
      }),
      whenBasketSettled: () =>
        when(
          () =>
            basketStore.productsToUpdate.length === 0 && basketStore.status !== StatusMap.PENDING,
          {timeout: BASKET_SETTLE_TIMEOUT_MS}
        ),
      onReady: buffer.attach,
      onDisabled: buffer.discard
    })

    return () => {
      stop()
      buffer.detach()
    }
  }, [oneTrustCheckCompleted, config])

  return (
    <DataCloudDispatchContext.Provider value={buffer.dispatch}>{children}</DataCloudDispatchContext.Provider>
  )
}

export const useDataCloudDispatch = () => useContext(DataCloudDispatchContext)
